import React, { useState, useEffect } from 'react';
import { projectAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import {
  Target,
  Calendar,
  DollarSign,
  Settings,
  CheckCircle2,
  Clock,
  Play,
  Pause,
  Flag,
  Edit,
  Save,
} from 'lucide-react';

const SDLC_MODELS = ['Agile', 'Scrum', 'Waterfall', 'Hybrid', 'Kanban', 'DevOps', 'Spiral'];
const PROJECT_PHASES = ['Initiation', 'Planning', 'Execution', 'Monitoring & Control', 'Closing'];

export default function ProjectOverview({ project, tasks, milestones, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: project?.name || '',
    description: project?.description || '',
    scope: project?.scope || '',
    budget: project?.budget || 0,
    budget_currency: project?.budget_currency || 'USD',
    start_date: project?.start_date || '',
    end_date: project?.end_date || '',
    sdlc_model: project?.sdlc_model || 'Agile',
    current_phase: project?.current_phase || 'Initiation',
  });

  // Sync formData when project prop updates
  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        scope: project.scope || '',
        budget: project.budget || 0,
        budget_currency: project.budget_currency || 'USD',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        sdlc_model: project.sdlc_model || 'Agile',
        current_phase: project.current_phase || 'Initiation',
      });
    }
  }, [project]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await projectAPI.update(project.id, formData);
      toast.success('Project updated successfully');
      setEditing(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update project');
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const totalTasks = tasks?.length || 0;
  const completedTasks = tasks?.filter(t => t.progress === 100).length || 0;
  const overallProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  
  const totalMilestones = milestones?.length || 0;
  const today = new Date().toISOString().split('T')[0];
  const completedMilestones = milestones?.filter(m => m.date <= today).length || 0;

  const getPhaseIndex = (phase) => PROJECT_PHASES.indexOf(phase);
  const currentPhaseIndex = getPhaseIndex(project?.current_phase || 'Initiation');

  return (
    <div className="space-y-6" data-testid="project-overview">
      {/* Header with Edit Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit text-2xl font-bold text-zinc-900">Project Overview</h2>
          <p className="text-zinc-600">Triple Constraints, Phases & Key Metrics</p>
        </div>
        <Button
          variant={editing ? "default" : "outline"}
          onClick={() => editing ? handleSave() : setEditing(true)}
          disabled={loading}
          data-testid="edit-overview-btn"
          className={editing ? "bg-primary hover:bg-orange-600 text-white" : ""}
        >
          {loading ? <span className="spinner" /> : editing ? (
            <><Save className="w-4 h-4 mr-2" /> Save Changes</>
          ) : (
            <><Edit className="w-4 h-4 mr-2" /> Edit Project</>
          )}
        </Button>
      </div>

      {/* Triple Constraints */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Scope */}
        <Card className="bg-white border-zinc-200 rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <span className="font-outfit">Scope</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {editing ? (
              <Textarea
                value={formData.scope}
                onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                placeholder="Define project scope, deliverables, and boundaries..."
                className="min-h-[120px] rounded-xl"
                data-testid="scope-input"
              />
            ) : (
              <p className="text-zinc-600 text-sm whitespace-pre-wrap">
                {project?.scope || 'No scope defined yet. Click Edit to add project scope.'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Schedule/Timeline */}
        <Card className="bg-white border-zinc-200 rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <span className="font-outfit">Schedule</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-500">Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="rounded-xl"
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-500">End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="rounded-xl"
                    data-testid="end-date-input"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-500">Start</span>
                  <span className="font-medium">{project?.start_date ? formatDate(project.start_date) : 'Not set'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-500">End</span>
                  <span className="font-medium">{project?.end_date ? formatDate(project.end_date) : 'Not set'}</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-500">Milestones</span>
                    <span className="text-sm font-medium">{completedMilestones}/{totalMilestones}</span>
                  </div>
                  <Progress value={totalMilestones > 0 ? (completedMilestones/totalMilestones)*100 : 0} className="h-2" />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Budget/Cost */}
        <Card className="bg-white border-zinc-200 rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <span className="font-outfit">Budget</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-500">Budget Amount</Label>
                  <Input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="rounded-xl"
                    data-testid="budget-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-500">Currency</Label>
                  <Select
                    value={formData.budget_currency}
                    onValueChange={(val) => setFormData({ ...formData, budget_currency: val })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="INR">INR (₹)</SelectItem>
                      <SelectItem value="JPY">JPY (¥)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div className="text-3xl font-bold text-zinc-900">
                  {project?.budget_currency === 'USD' && '$'}
                  {project?.budget_currency === 'EUR' && '€'}
                  {project?.budget_currency === 'GBP' && '£'}
                  {project?.budget_currency === 'INR' && '₹'}
                  {project?.budget_currency === 'JPY' && '¥'}
                  {(project?.budget || 0).toLocaleString()}
                </div>
                <p className="text-sm text-zinc-500">Total Budget ({project?.budget_currency || 'USD'})</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SDLC Model & Current Phase */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* SDLC Model */}
        <Card className="bg-white border-zinc-200 rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <Settings className="w-5 h-5 text-purple-600" />
              </div>
              <span className="font-outfit">SDLC Model</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {editing ? (
              <Select
                value={formData.sdlc_model}
                onValueChange={(val) => setFormData({ ...formData, sdlc_model: val })}
              >
                <SelectTrigger className="rounded-xl" data-testid="sdlc-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SDLC_MODELS.map(model => (
                    <SelectItem key={model} value={model}>{model}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-purple-600">{project?.sdlc_model || 'Agile'}</span>
                <span className="text-sm text-zinc-500">Development Methodology</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Phase */}
        <Card className="bg-white border-zinc-200 rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                <Flag className="w-5 h-5 text-orange-600" />
              </div>
              <span className="font-outfit">Current Phase</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {editing ? (
              <Select
                value={formData.current_phase}
                onValueChange={(val) => setFormData({ ...formData, current_phase: val })}
              >
                <SelectTrigger className="rounded-xl" data-testid="phase-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_PHASES.map(phase => (
                    <SelectItem key={phase} value={phase}>{phase}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-orange-600">{project?.current_phase || 'Initiation'}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Project Lifecycle Phases */}
      <Card className="bg-white border-zinc-200 rounded-xl">
        <CardHeader>
          <CardTitle className="font-outfit text-lg">Project Lifecycle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            {PROJECT_PHASES.map((phase, idx) => {
              const isCompleted = idx < currentPhaseIndex;
              const isCurrent = idx === currentPhaseIndex;
              
              return (
                <React.Fragment key={phase}>
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-100' : isCurrent ? 'bg-orange-100' : 'bg-zinc-100'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      ) : isCurrent ? (
                        <Play className="w-6 h-6 text-orange-600" />
                      ) : (
                        <Clock className="w-6 h-6 text-zinc-400" />
                      )}
                    </div>
                    <span className={`text-xs mt-2 text-center max-w-[80px] ${
                      isCurrent ? 'font-semibold text-orange-600' : isCompleted ? 'text-green-600' : 'text-zinc-500'
                    }`}>
                      {phase}
                    </span>
                  </div>
                  {idx < PROJECT_PHASES.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded ${
                      idx < currentPhaseIndex ? 'bg-green-300' : 'bg-zinc-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-50 border-zinc-200 rounded-xl p-4">
          <div className="text-3xl font-bold text-zinc-900">{totalTasks}</div>
          <div className="text-sm text-zinc-500">Total Tasks</div>
        </Card>
        <Card className="bg-green-50 border-green-200 rounded-xl p-4">
          <div className="text-3xl font-bold text-green-600">{completedTasks}</div>
          <div className="text-sm text-green-700">Completed</div>
        </Card>
        <Card className="bg-blue-50 border-blue-200 rounded-xl p-4">
          <div className="text-3xl font-bold text-blue-600">{overallProgress}%</div>
          <div className="text-sm text-blue-700">Progress</div>
        </Card>
        <Card className="bg-amber-50 border-amber-200 rounded-xl p-4">
          <div className="text-3xl font-bold text-amber-600">{totalMilestones}</div>
          <div className="text-sm text-amber-700">Milestones</div>
        </Card>
      </div>
    </div>
  );
}
